import { moduleActions } from './objectActions/moduleActions';
import { userActions } from './objectActions/userActions';
import { genericActionConfigs } from './objectActions/genericActions';
import { createModuleApprovalActions } from './objectActions/moduleApprovals';
import { submissionActions } from './objectActions/submissionActions';
import type { ActionConfig, ActionHandlerProps } from './DataTable/actionTypes';

export { type ActionConfig, type ActionHandlerProps };

export interface ObjectConfig {
  id: string;
  excludeGeneric?: string[];
  actions?: Record<string, Partial<ActionConfig>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actionCreator?: (hooks: unknown) => Record<string, Partial<ActionConfig>>;
}

export const objectActionsConfig: Record<string, ObjectConfig> = {
  submissions: {
    id: 'reports/submissions',
    excludeGeneric: ['create', 'edit', 'delete', 'toggleDeprecated', 'changeState'],
    actions: {
      overwriteAdminGrade: submissionActions.overwriteAdminGrade,
      returnSubmissionToStudent: submissionActions.returnSubmissionToStudent,
      returnSubmissionToLecturer: submissionActions.returnSubmissionToLecturer,
      upgradeSubmissionToOral: submissionActions.upgradeSubmissionToOral,
      grantAdditionalAttempt: submissionActions.grantAdditionalAttempt,
      deleteSubmission: submissionActions.deleteSubmission,
    },
  },
  'reports/users/[0-9]+/modules': {
    id: 'reports/users/[0-9]+/modules',
    excludeGeneric: ['create', 'edit', 'delete', 'toggleDeprecated', 'changeState'],
    actions: {
      setAdminGrade: submissionActions.setAdminGrade,
      overwriteAdminGrade: submissionActions.overwriteAdminGrade,
      returnSubmissionToStudent: submissionActions.returnSubmissionToStudent,
      returnSubmissionToLecturer: submissionActions.returnSubmissionToLecturer,
      upgradeSubmissionToOral: submissionActions.upgradeSubmissionToOral,
      grantAdditionalAttempt: submissionActions.grantAdditionalAttempt,
      deleteSubmission: submissionActions.deleteSubmission,
    },
  },
  enrollments: {
    id: 'reports/enrollments',
    excludeGeneric: ['create', 'edit', 'delete', 'toggleDeprecated', 'changeState'],
  },
  modules: {
    id: 'modules',
    excludeGeneric: ['toggleDeprecated'], // Custom implementation due to strict API schema
    actions: moduleActions as unknown as Record<string, Partial<ActionConfig>>,
  },
  users: {
    id: 'users',
    actions: userActions as unknown as Record<string, Partial<ActionConfig>>,
  },
  'modules/approvals': {
    id: 'modules/approvals',
    excludeGeneric: ['create', 'edit', 'delete', 'toggleDeprecated', 'changeState'],
    actionCreator: createModuleApprovalActions,
  },
};

export const mergeWithGenericActions = (
  customActions: Record<string, Partial<ActionConfig>> | ActionConfig[],
  excludeGeneric: string[] = [],
) => {
  const genericActions = Object.fromEntries(
    Object.entries(genericActionConfigs)
      .filter(([key]) => !excludeGeneric.includes(key))
      .map(([key, action]) => [
        key,
        {
          ...action,
          position: action.position || 'row',
          variant: 'solid',
          action: key === 'create' ? 'primary' : 'secondary',
        },
      ]),
  );

  const normalizedCustomActions = Array.isArray(customActions)
    ? Object.fromEntries(customActions.map((action) => [action.id, { ...action, position: action.position || 'row' }]))
    : Object.fromEntries(
      Object.entries(customActions || {}).map(([key, action]) => [
        key,
        { ...action, position: action.position || 'row' },
      ]),
    );

  const { create: createAction, delete: deleteAction, ...otherGenericActions } = genericActions;

  return {
    ...otherGenericActions,
    ...normalizedCustomActions,
    ...(deleteAction ? { delete: deleteAction } : {}),
    ...(createAction ? { create: createAction } : {}),
  };
};

export const getObjectActions = (objectName: string, hookFunctions: Record<string, unknown> = {}) => {
  const config = objectActionsConfig[objectName]
    || Object.values(objectActionsConfig).find((c) => c.id === objectName)
    || {
      id: objectName,
      actions: {},
      excludeGeneric: [] as string[],
      actionCreator: undefined as ((hooks: unknown) => Record<string, Partial<ActionConfig>>) | undefined,
    };

  const actions = config.actionCreator && hookFunctions[objectName]
    ? config.actionCreator(hookFunctions[objectName])
    : config.actions || {};

  return mergeWithGenericActions(actions, config.excludeGeneric || []);
};
